import {
  Transaction,
} from "@/types/pos/transaction";
import {
  SalesReport,
} from "@/types/pos/sales-history"
import React, { ReactNode } from "react";
import logomainsvg from "@/assets/logo-white.svg";

interface ReceiptProps {
  salesReport: SalesReport;
}
import { capitalizeFirstLetter, displayDate, displayTime, formatNIC, formatToPKR, hasFullAccessToMemberNIC } from "@/lib/helper";
import { InvoiceDetails } from "@/types/invoice";
import { SaleStatus } from "@/types/enums";

const Receipt: React.FC<ReceiptProps> = ({ salesReport }) => {
  const isRefund = salesReport.transaction_type == SaleStatus.Refund;

  return (
    <div className="p-6 border border-gray-300 rounded-lg shadow-md max-w-sm mx-auto">
      <h2 className="text-xl font-bold text-center mb-4">
        {isRefund ? "Refund Receipt" : "Sale Receipt"}
      </h2>
      <div className="flex flex-col  gap-2  justify-center items-center">
        <p>Receipt Number: {salesReport.receipt_number}</p>
        <p>Transaction Date: {salesReport?.transaction_date as ReactNode}</p>
      </div>

      <h4 className="font-semibold mt-2 mb-2">Member Details</h4>
      <div className="flex justify-center gap-1 flex-col items-start">
        <p>Name: {capitalizeFirstLetter(salesReport.member_name)}</p>
        <p>Email: {capitalizeFirstLetter(salesReport.member_email)}</p>
        <p>Address: {capitalizeFirstLetter(salesReport.member_address)}</p>
        <p>Gender: {capitalizeFirstLetter(salesReport.member_gender)}</p>
      </div>

      <hr className="my-4" />

      <h4 className="font-semibold">Items</h4>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-200">
            <th className="border border-gray-300 px-2 py-1">Name</th>
            <th className="border border-gray-300 px-2 py-1">Quantity</th>
            <th className="border border-gray-300 px-2 py-1">Price</th>
            <th className="border border-gray-300 px-2 py-1">Total</th>
          </tr>
        </thead>
        <tbody>
          {salesReport.items?.map((item) => (
            <tr key={item.item_id}>
              <td className="border border-gray-300 px-2 py-1">
                {capitalizeFirstLetter(item.description ?? "")}
              </td>
              <td className="border border-gray-300 px-2 py-1">
                {item.quantity}
              </td>
              <td className="border border-gray-300 px-2 py-1">
                {item.price?.toFixed(2)}
              </td>
              <td className="border border-gray-300 px-2 py-1">
                {item.total?.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <hr className="my-4" />
      <h4 className="font-semibold pb-2">Summary</h4>
      <div className="flex flex-col justify-center items-start gap-2">
        <p>
          Subtotal:{" "}
          <span className="font-bold">{salesReport.sub_total.toFixed(2)}</span>
        </p>
        <p>
          Discount:{" "}
          <span className="font-bold">
            {salesReport.discount.toFixed(2)}
          </span>
        </p>
        <p>
          Tax Amount:{" "}
          <span className="font-bold">{salesReport.tax_amount.toFixed(2)}</span>
        </p>
        {/* <h4 className="font-bold"> */}
        <h4>
          Total:{" "}
          <span className="text-lg font-bold">
            {salesReport.total.toFixed(2)}
          </span>
        </h4>

        <p>Notes: {capitalizeFirstLetter(salesReport.notes)}</p>
      </div>
    </div>
  );
};

export default Receipt;

export function ReceiptExport(salesReport: SalesReport | Transaction) {
  const { user } = JSON.parse(localStorage.getItem("userInfo") as string);
  const isOwner = hasFullAccessToMemberNIC();
  return user && salesReport && `<!DOCTYPE html>
<html lang="en">

<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Receipt</title>
    <style>
    @page {
      margin: 0 0 40px 0;
      size: auto;
    }
            
    @page :first {
      margin-top: 0;
    }
            
    @page :left {
      margin-left: 0;
    }
            
    @page :right {
      margin-right: 0;
    }
                    
    @media print {
      @page {
        margin: 0 0 40px 0;
        size: auto;
      }    
    }

    /* Common Styles */
    * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }
    .price {
        text-align: right !important;
    }

    /* Default / A4 Styles (Old Design) */
    .thermal-receipt {
        display: none;
    }

    .a4-receipt {
        display: block;
    }

    body {
        font-family: Arial, sans-serif;
        margin: 0;
        padding: 20px 20px 60px 20px;
    }

    /* ── A4 New Design (mirrors thermal layout) ── */
    .a4-container {
        margin: 0 auto;
        background-color: #ffffff;
        padding: 20px;
        font-size: 14px;
        color: #000;
    }

    .a4-header {
        text-align: center;
        margin-bottom: 6px;
    }

    .a4-header h2 {
        margin: 0;
        font-size: 22px;
        font-weight: bold;
        text-transform: uppercase;
    }

    .a4-banner {
        background: #000;
        color: #fff;
        text-align: center;
        font-weight: bold;
        padding: 6px 0;
        margin: 8px 0;
        font-size: 14px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .a4-detail-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 10px;
        font-size: 13px;
    }

    .a4-detail-table td {
        padding: 3px 0;
        border: none;
        vertical-align: top;
    }

    .a4-detail-table td:first-child {
        font-weight: bold;
        width: 40%;
    }

    .a4-detail-table td:last-child {
        text-transform: uppercase;
    }

    .a4-items-table {
        width: 100%;
        border-collapse: collapse;
        margin: 8px 0;
        font-size: 13px;
    }

    .a4-items-table th {
        background: #000;
        color: #fff;
        font-weight: bold;
        padding: 7px 6px;
        text-transform: uppercase;
        text-align: left;
        border: none;
    }

    .a4-items-table th.price {
        text-align: right;
    }

    .a4-items-table td {
        border-bottom: 1px dashed #ccc;
        padding: 7px 6px;
        vertical-align: top;
        text-align: left;
    }

    .a4-items-table td.price {
        text-align: right;
    }

    .a4-divider {
        border: none;
        border-top: 1px dashed #000;
        margin: 10px 0;
    }

    .a4-summary-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
        margin-top: 8px;
    }

    .a4-summary-table td {
        padding: 4px 6px;
        border: none;
    }

    .a4-summary-table td:last-child {
        text-align: right;
    }

    .a4-grand-total td {
        font-weight: bold;
        font-size: 14px;
        border-top: 1px dashed #000;
        border-bottom: 1px dashed #000;
        padding: 6px 6px;
    }

    .a4-footer {
        margin-top: 20px;
        text-align: center;
        font-size: 13px;
        color: #000;
    }

    .a4-thank-you {
        font-weight: bold;
        font-size: 15px;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 8px;
    }

    .a4-footer-datetime {
        width: 100%;
        border-collapse: collapse;
        border-top: 1px dashed #000;
        margin-top: 6px;
    }

    .a4-footer-datetime td {
        padding-top: 5px;
        border: none;
        font-size: 12px;
    }

    /* Thermal 80mm Print Query (Chase Up Design) */
    @media print and (max-width: 100mm) {
        .a4-receipt {
            display: none !important;
        }

        .thermal-receipt {
            display: block !important;
        }

        @page {
            margin: 0;
            size: auto;
        }

        body {
            font-family: Arial, sans-serif;
            width: 80mm;
            max-width: 80mm;
            margin: 0 auto;
            padding: 8px;
            color: #000;
            background-color: #ffffff;
            box-sizing: border-box;
        }

        .thermal-container {
            width: 100%;
            margin: 0 auto;
        }

        .thermal-header {
            text-align: center;
            margin-bottom: 8px;
        }

        .thermal-logo {
            width: 55px;
            height: auto;
            display: block;
            margin: 0 auto 6px;
        }

        .thermal-header h2 {
            margin: 0;
            font-size: 16px;
            font-weight: bold;
            text-transform: uppercase;
            line-height: 1.2;
        }

        .thermal-banner {
            background: #000;
            color: #fff;
            text-align: center;
            font-weight: bold;
            padding: 5px 0;
            margin: 8px 0;
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .thermal-details {
            margin-bottom: 8px;
            font-size: 11px;
            line-height: 1.4;
        }

        .thermal-detail-row {
            display: table;
            width: 100%;
            margin-bottom: 3px;
        }

        .thermal-detail-label {
            display: table-cell;
            font-weight: bold;
            white-space: nowrap;
            padding-right: 4px;
            vertical-align: top;
        }

        .thermal-detail-value {
            display: table-cell;
            text-align: right;
            text-transform: uppercase;
            word-break: break-all;
            vertical-align: top;
        }

        .thermal-table-container {
            width: 100%;
            border-collapse: collapse;
            margin: 8px 0;
            font-size: 11px;
        }

        .thermal-table-header-row {
            background: #000;
            color: #fff;
        }

        .thermal-table-container th {
            background: #000;
            color: #fff;
            font-weight: bold;
            padding: 6px 4px;
            text-transform: uppercase;
            text-align: left;
            border: none;
        }

        .thermal-table-container td {
            border-bottom: 1px dashed #ccc;
            padding: 6px 4px;
            vertical-align: top;
            text-align: left;
        }

        .thermal-divider {
            border: none;
            border-top: 1px dashed #000;
            margin: 8px 0;
        }

        .thermal-summary {
            font-size: 11px;
            line-height: 1.4;
            margin-top: 8px;
        }

        .thermal-summary-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 4px;
        }

        .thermal-summary-row span:first-child {
            font-weight: normal;
        }

        .thermal-summary-row span:last-child {
            text-align: right;
        }

        .thermal-grand-total {
            font-weight: bold;
            font-size: 13px;
            border-top: 1px dashed #000;
            border-bottom: 1px dashed #000;
            padding: 5px 0;
            margin: 6px 0;
        }

        .thermal-grand-total span {
            font-weight: bold !important;
        }

        .thermal-footer {
            margin-top: 15px;
            text-align: center;
            font-size: 11px;
            color: #000;
        }

        .thermal-thank-you {
            font-weight: bold;
            margin-bottom: 8px;
            text-transform: uppercase;
            letter-spacing: 1px;
        }

        .thermal-footer-datetime {
            display: flex;
            justify-content: space-between;
            border-top: 1px dashed #000;
            padding-top: 5px;
        }

        .thermal-spacer {
            height: 40px;
        }
    }
    </style>
</head>

<body>
    <!-- A4 Receipt (Thermal-style design) -->
    <div class="a4-receipt">
        <div class="a4-container">
            <!-- Header: Org Name -->
            <div class="a4-header">
                <h2>${user?.org_name}</h2>
            </div>
            <!-- Banner: Sale/Refund Receipt -->
            <div class="a4-banner">
                ${salesReport.transaction_type == SaleStatus.Refund ? "Refund Receipt" : "Sale Receipt"}
            </div>
            <!-- Details: left label, right value -->
            <table class="a4-detail-table">
                <tr><td>SRB Invoice No:</td><td>${salesReport.srb_invoice_id ?? ""}</td></tr>
                <tr><td>Transaction ID:</td><td>${salesReport.receipt_number ?? ""}</td></tr>
                <tr><td>Date:</td><td>${displayDate(salesReport.created_at)} ${displayTime(salesReport.created_at)}</td></tr>
                <tr><td>Name:</td><td>${salesReport.member_name}</td></tr>
                ${salesReport.member_nic && isOwner ? `<tr><td>${salesReport.is_business ? "NTN" : "CNIC"}:</td><td>${salesReport.is_business ? salesReport.member_nic : formatNIC(salesReport.member_nic)}</td></tr>` : ""}
                <tr><td>Transaction Type:</td><td>${salesReport.transaction_type == "Sale" ? "Receipt" : salesReport.transaction_type}</td></tr>
                <tr><td>Status:</td><td>${salesReport.status}</td></tr>
            </table>
            <!-- Items Table with black header -->
            <table class="a4-items-table">
                <thead>
                    <tr>
                        <th style="width:10%;">Qty</th>
                        <th style="width:50%;">Item</th>
                        <th class="price" style="width:20%;">Price</th>
                        <th class="price" style="width:20%;">Total</th>
                    </tr>
                </thead>
                <tbody>
                    ${salesReport?.items
                      ?.filter(item => item)
                      .map((item) =>
                        `<tr>
                            <td>${item.quantity}</td>
                            <td style="text-transform:capitalize;">${item.description}</td>
                            <td class="price">${formatToPKR(item.price)}</td>
                            <td class="price">${formatToPKR(item.sub_total)}</td>
                        </tr>`
                      ).join('')}
                </tbody>
            </table>
            <hr class="a4-divider" />
            <!-- Summary -->
            <table class="a4-summary-table">
                <tr>
                    <td>No of Items:</td>
                    <td>${salesReport.items?.filter(Boolean).length ?? 0}</td>
                </tr>
                <tr>
                    <td>Sub Total:</td>
                    <td>${formatToPKR(salesReport.sub_total as number)}</td>
                </tr>
                <tr>
                    <td>Discount:</td>
                    <td>${formatToPKR(salesReport.discount as number)}</td>
                </tr>
                <tr>
                    <td>Sindh Sales Tax:</td>
                    <td>${formatToPKR(salesReport.tax_amount as number)}</td>
                </tr>
                <tr class="a4-grand-total">
                    <td>Grand Total:</td>
                    <td>${formatToPKR((salesReport.transaction_type == SaleStatus.Refund ? -Number(salesReport.total) : salesReport.total) as number)}</td>
                </tr>
                ${salesReport?.payments
                  ?.filter(payment => payment && payment.payment_method)
                  .map((payment) =>
                    `<tr>
                        <td style="text-transform:capitalize;">${payment.payment_method}:</td>
                        <td>${formatToPKR(payment.payment_method === "Cash" ? (payment.amount + salesReport.change_amount) : payment.amount)}</td>
                    </tr>`
                  ).join('')}
                ${Number(salesReport.change_amount) > 0 ? `
                <tr>
                    <td>Change Given:</td>
                    <td>${formatToPKR(salesReport.change_amount as number)}</td>
                </tr>` : ""}
            </table>
            <!-- Footer -->
            <div class="a4-footer">
                <div class="a4-thank-you">THANK YOU</div>
                <table class="a4-footer-datetime">
                    <tr>
                        <td style="text-align:left;">Date: ${displayDate(salesReport.created_at)}</td>
                        <td style="text-align:right;">Time: ${displayTime(salesReport.created_at)}</td>
                    </tr>
                </table>
            </div>
        </div>
    </div>

    <!-- Thermal Receipt (Chase Up Design) -->
    <div class="thermal-receipt">
        <div class="thermal-container">
            <div class="thermal-header">
                <h2>${user?.org_name}</h2>
                <div class="thermal-banner">
                    ${salesReport.transaction_type == SaleStatus.Refund ? "Refund Receipt" : "Sale Receipt"}
                </div>
            </div>
            <div class="thermal-details">
                <div class="thermal-detail-row"><span class="thermal-detail-label">SRB Invoice No:</span> <span class="thermal-detail-value">${salesReport.srb_invoice_id ?? ""}</span></div>
                <div class="thermal-detail-row"><span class="thermal-detail-label">Transaction ID:</span> <span class="thermal-detail-value">${salesReport.receipt_number ?? ""}</span></div>
                <div class="thermal-detail-row"><span class="thermal-detail-label">Date:</span> <span class="thermal-detail-value">${displayDate(salesReport.created_at)}</span></div>
                <div class="thermal-detail-row"><span class="thermal-detail-label">Name:</span> <span class="thermal-detail-value">${salesReport.member_name}</span></div>
                ${salesReport.member_nic && isOwner ? `<div class="thermal-detail-row"><span class="thermal-detail-label">${salesReport.is_business ? "NTN" : "CNIC"}:</span> <span class="thermal-detail-value">${salesReport.is_business ? salesReport.member_nic : formatNIC(salesReport.member_nic)}</span></div>` : ""}
                <div class="thermal-detail-row"><span class="thermal-detail-label">Transaction Type:</span> <span class="thermal-detail-value">${salesReport.transaction_type == "Sale" ? "Receipt" : salesReport.transaction_type}</span></div>
                <div class="thermal-detail-row"><span class="thermal-detail-label">Status:</span> <span class="thermal-detail-value">${salesReport.status}</span></div>
            </div>
            <table class="thermal-table-container">
                <thead>
                    <tr class="thermal-table-header-row">
                        <th style="width: 10%;">Qty</th>
                        <th style="width: 50%;">Item</th>
                        <th class="price" style="width: 20%;">Price</th>
                        <th class="price" style="width: 20%;">Total</th>
                    </tr>
                </thead>
                <tbody>
                    ${salesReport?.items
                      ?.filter(item => item)
                      .map((item) =>
                        `<tr>
                            <td>${item.quantity}</td>
                            <td style="text-transform: capitalize;">${item.description}</td>
                            <td class="price">${formatToPKR(item.price)}</td>
                            <td class="price">${formatToPKR(item.sub_total)}</td>
                        </tr>`
                      ).join('')}
                </tbody>
            </table>
            <hr class="thermal-divider" />
            <div class="thermal-summary">
                <div class="thermal-summary-row">
                    <span>No of Items:</span>
                    <span>${salesReport.items?.filter(Boolean).length ?? 0}</span>
                </div>
                <div class="thermal-summary-row">
                    <span>Sub Total:</span>
                    <span>${formatToPKR(salesReport.sub_total as number)}</span>
                </div>
                <div class="thermal-summary-row">
                    <span>Discount:</span>
                    <span>${formatToPKR(salesReport.discount as number)}</span>
                </div>
                <div class="thermal-summary-row">
                    <span>Sindh Sales Tax:</span>
                    <span>${formatToPKR(salesReport.tax_amount as number)}</span>
                </div>
                <div class="thermal-summary-row thermal-grand-total">
                    <span>Grand Total:</span>
                    <span>${formatToPKR((salesReport.transaction_type == SaleStatus.Refund ? -Number(salesReport.total) : salesReport.total) as number)}</span>
                </div>
                ${salesReport?.payments
                  ?.filter(payment => payment && payment.payment_method)
                  .map((payment) =>
                    `<div class="thermal-summary-row">
                        <span style="text-transform: capitalize;">${payment.payment_method}:</span>
                        <span>${formatToPKR(payment.payment_method === "Cash" ? (payment.amount + salesReport.change_amount) : payment.amount)}</span>
                    </div>`
                  ).join('')}
                ${Number(salesReport.change_amount) > 0 ? `
                    <div class="thermal-summary-row">
                        <span>Change Due:</span>
                        <span>${formatToPKR(salesReport.change_amount as number)}</span>
                    </div>` : ""}
            </div>
            <!-- Thank You & Footer Details -->
            <div class="thermal-footer">
                <div class="thermal-thank-you">THANK YOU</div>
                <div class="thermal-footer-datetime">
                    <span>Date: ${displayDate(salesReport.created_at)}</span>
                    <span>Time: ${displayTime(salesReport.created_at)}</span>
                </div>
                <div class="thermal-spacer"></div>
            </div>
        </div>
    </div>
</body>

</html>`;
};
export function InvoiceExport(invoice: InvoiceDetails) {
  const { user } = JSON.parse(localStorage.getItem("userInfo") as string);
  const isOwner = hasFullAccessToMemberNIC();

  return user && invoice && `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>

     @page {
      margin: 0 0 40px 0;
      size: auto;
    }
            
    @page :first {
      margin-top: 0;
    }
            
    @page :left {
      margin-left: 0;
    }
            
    @page :right {
      margin-right: 0;
    }
            
    body {
      margin: 0;
      padding: 20px;
      font-family: Arial, sans-serif;
    }
            
    @media print {
      @page {
        margin: 0 0 40px 0;
        size: auto;
      }   
    }    
      body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 20px 20px 60px 20px;
      }
      
      .card {
        background-color: white;
        margin: 0 auto;
      }
      .card-header {
        padding: 20px;
        margin:auto 20px;
        border-bottom: 1px solid #e0e0e0;
      }
      .card-header h2 {
        margin: 0;
        font-size: 1.25rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .close-btn {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
      }
      .action-buttons {
        display: flex;
        gap: 10px;
        margin-top: 15px;
      }
      .btn {
        padding: 8px 12px;
        border: 1px solid #ccc;
        background-color: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.875rem;
      }
      .btn-delete {
        color: #e11d48;
      }
      .tabs {
        display: flex;
        border-bottom: 1px solid #e0e0e0;
        margin-top: 15px;
      }
      .tab {
        padding: 10px 15px;
        cursor: pointer;
      }
      .tab.active {
        border-bottom: 2px solid #2563eb;
        color: #2563eb;
      }
      .card-content {
        padding: 20px;
      }
      .amount {
        margin-bottom: 20px;
      }
      .amount-label {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 0.875rem;
        color: #666;
      }
      .badge {
        background-color: #fef3c7;
        color: #d97706;
        padding: 2px 8px;
        border-radius: 9999px;
        font-size: 0.75rem;
      }
      .amount-value {
        font-size: 2rem;
        font-weight: bold;
        color: #e11d48;
        margin-top: 5px;
      }
      .details-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
        margin-bottom: 20px;
      }
      .detail-item h4 {
        margin: 0;
        font-size: 0.875rem;
        color: #666;
      }
      .detail-item p {
        margin: 5px 0 0;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 20px;
      }
      th,
      td {
        text-align: left;
        padding: 10px;
        border-bottom: 1px solid #e0e0e0;
      }
      th {
        font-weight: normal;
        color: #666;
      }
      .text-right {
        text-align: right;
      }
      .totals {
        padding-top: 20px;
        width: 40%;
        margin-left: auto;
      }
      .total-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
      }
      .total-row.bold {
        font-weight: bold;
      }
      .total-row.muted {
        color: #666;
      }
      @media (max-width: 900px) {
        .details-grid {
          grid-template-columns: repeat(2, 1fr);
        }
        .action-buttons {
          flex-wrap: wrap;
        }
        .btn {
          flex: 1 0 calc(50% - 5px);
        }
      }
    </style>
  </head>
  <body>
    <div class="card">
      <div class="card-header">
        <h2 style="width: fit-content; margin: auto">
          ${user.org_name}
        </h2>
        <!--<p style="width: fit-content; margin: auto">
          500 yong Street north york
        </p>-->
        <!--<p style="width: fit-content; margin: auto">info@atmosphere.com</p>-->
      </div>

      <div class="card-content">
        <div class="details-grid">
          <div class="detail-item">
            <h4>Bill To</h4>
            <p style="text-transform: capitalize;">${invoice.member_name}</p>
          </div>
          <div class="detail-item">
            <h4>Email</h4>
            <p>${invoice.member_email}</p>
          </div>
          ${invoice.member_nic && isOwner ? `<div class="detail-item">
            <h4>${invoice.is_business ? "NTN" : "CNIC"}</h4>
            <p>${invoice.is_business ? invoice.member_nic : formatNIC(invoice.member_nic)}</p>
          </div>` : ""}
          <div class="detail-item">
            <h4>Invoice Number</h4>
            <p>${invoice.invoice_number ?? ""}</p>
          </div>
          

          <div class="detail-item">
            <h4>Invoice Date</h4>
            <p>${displayDate(invoice.created_at)}</p>
          </div>
          
        </div>
        <table>
          <thead >
            <tr>
              <th style="text-wrap:nowrap;">Product</th>
              <th style="text-wrap:nowrap;">Name</th>
              <th style="text-wrap:nowrap;">Book Date</th>
              <th style="text-wrap:nowrap;" class="text-right">Qty</th>
              <th style="text-wrap:nowrap;" class="text-right">Unit Price</th>
              <th style="text-wrap:nowrap;" class="text-right">Net Amount</th>
              <th style="text-wrap:nowrap;" class="text-right">Discount</th>
              <th style="text-wrap:nowrap;" class="text-right">Tax Rate</th>
              <th style="text-wrap:nowrap;" class="text-right">Total</th>
            </tr>
          </thead>
          <tbody >
          ${invoice.items.map(membership => membership ?
    `<tr>
              <td style="text-wrap:nowrap;">${membership.item_type}</td>
              <td style="text-wrap:nowrap; text-transform: capitalize;">${membership.description}</td>
              <td style="text-wrap:nowrap;">${displayDate(membership.booking_date)}</td>
              <td style="text-wrap:nowrap;" class="text-right">${membership.quantity}</td>
              <td style="text-wrap:nowrap;" class="text-right">${formatToPKR(membership.price)}</td>
              <td style="text-wrap:nowrap;" class="text-right">${formatToPKR(membership.sub_total)}</td>
              <td style="text-wrap:nowrap;" class="text-right">${formatToPKR(membership.discount)}</td>
              <td style="text-wrap:nowrap;" class="text-right">${membership.tax_rate?.toFixed(2)}%</td>
              <td style="text-wrap:nowrap;" class="text-right">${formatToPKR(membership.total)}</td>
            </tr>`: ""
  ).join('')}  
          
          </tbody>
        </table>
        <div class="totals">
          <div class="total-row bold">
            <span>Subtotal</span>
            <span>${formatToPKR(invoice.sub_total)}</span>
          </div>
          <div class="total-row bold">
            <span>Total Discount </span>
            <span>${formatToPKR(invoice.discount)}</span>
          </div>
          <div class="total-row bold">
            <span>Tax Amount </span>
            <span>${formatToPKR(invoice.tax_amount)}</span>
          </div>
          <div class="total-row bold">
            <span>Total Amount </span>
            <span>${formatToPKR(invoice.total)}</span>
          </div>
        </div>
      </div>
    </div>
  </body>
</html>
`;
}